import { isPlatformBrowser } from "@angular/common";
import { Inject, Injectable, Optional, PLATFORM_ID } from "@angular/core";
import { BehaviorSubject } from "rxjs";
import * as cryptoJs from 'crypto-js'; // Import the crypto-js library
import { DOCUMENT } from '@angular/common';
import { SERVER_DATA } from "../tokens/server-data.token";
import { environment } from "src/environments/environment";
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

@Injectable({
  providedIn: "root",
})
export class SharedService {
  public updateProfile$ = new BehaviorSubject<any>(null);    
  public balanceResponse$ = new BehaviorSubject<any>(null);    
  public balance$ = new BehaviorSubject<any>(null);  
  public userImg$ = new BehaviorSubject<any>(null);  
  public redirectObj$ = new BehaviorSubject<any>(null);  
  public loaderFlag$ = new BehaviorSubject<any>({
    value: false,
    startImediate: false,
  });
  public loaderFlagWithMsg$ = new BehaviorSubject<any>({
    value: false,
    startImediate: false,
  });
    public toastFlag$ = new BehaviorSubject<any>({
    value: false,
    startImediate: false,
    topLayer: false,
  });
  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    @Inject(DOCUMENT) private document: Document,
    @Optional() @Inject('REQUEST_ORIGIN') private origin: string,
    @Optional() @Inject(SERVER_DATA) private serverData: any    
  ) {}

  enableFullLoader(startImediate: boolean = false) {
    this.loaderFlag$.next({ value: true, startImediate: startImediate });
  }

  redirect(url: string){    
    this.redirectObj$.next(this.parseUrl(url));
  }

  private parseUrl(url: string) {
    try {
      const urlObj = new URL(url, window.location.origin);
      const queryParams: Record<string, string> = {};
      urlObj.searchParams.forEach((value, key) => {
        queryParams[key] = value;
      });
      return { url: urlObj.pathname, queryParams };
    } catch {
      // fallback if URL is invalid
      return { url: url, queryParams: {} };
    }
  }

  disableFullLoader() {
    this.loaderFlag$.next({ value: false, startImediate: false });
    this.disableFullLoaderInitial();
  }

  triggerProfileUpdate(data: any = true) {
  this.updateProfile$.next(data);
}

  enableFullLoaderWithMsg(msg: string = "") {
    this.loaderFlagWithMsg$.next({ value: true, startImediate: false, msg: msg });
  }

  disableFullLoaderWithMsg() {
    this.loaderFlagWithMsg$.next({ value: false, startImediate: false, msg: '' });
    this.disableFullLoaderInitial();
  }


  enableToast(msg: string = "", toastType: string = "green", topLayer: boolean = false) {
    // toastType => green / red
    this.toastFlag$.next({ value: true, startImediate: false, msg: msg, toastType: toastType, topLayer: topLayer });
  }

  disableToast() {
    this.toastFlag$.next({ value: false, startImediate: false, msg: '', toastType: '', topLayer: false });
  }
  

  disableFullLoaderInitial() {
    if (typeof window === "undefined") return;
    const fullLoaderInitialLandingEle = document.querySelector(
      ".full-loader-initial-landing"
    );
    if (fullLoaderInitialLandingEle)
      fullLoaderInitialLandingEle.classList.add("hidden");
  }

  vistNode(category: string, id: string) {
    if (isPlatformBrowser(this.platformId)) {
      let savedData = localStorage.getItem(category)
        ? JSON.parse(localStorage.getItem(category) || "")
        : [];
      savedData.push(id);
      localStorage.setItem(category, JSON.stringify(savedData));
    }
  }

  isVistedNode(category: string, id: string) {
    if (isPlatformBrowser(this.platformId)) {
      let savedData = localStorage.getItem(category)
        ? JSON.parse(localStorage.getItem(category) || "")
        : [];
      return savedData.some((item: any) => item === id) || false;
    }
  }

  isValidImage(img: string) {
    return img && img !== "00000000-0000-0000-0000-000000000000" ? img : null;
  }

  isExternalMethod(link: string) {
    if (!link) return false;
    if (
      link.startsWith("//") ||
      link.startsWith("http://") ||
      link.startsWith("https://") ||
      link.startsWith("www.")
    ) {
      return true;
    }
    return false;
  }

  encryptAmount(amount: string): string {
    const secretKey = 'woqod';
    const ciphertext = cryptoJs.AES.encrypt(amount, secretKey).toString();
    return encodeURIComponent(ciphertext); // Safe for URLs
  }
  
  decryptAmount(encrypted: any): string {
    const secretKey = 'woqod';
    const bytes = cryptoJs.AES.decrypt(decodeURIComponent(encrypted), secretKey);
    return bytes.toString(cryptoJs.enc.Utf8);
  }  


  getDomainURL(){
    if (isPlatformBrowser(this.platformId)) {
      const locationObj = window.location;
      const pathSegments = locationObj.pathname.split('/').filter(Boolean); // removes empty strings
      const firstPathSegment = pathSegments?.[0];
      const baseDomain = pathSegments.length > 0 ? `${locationObj.origin}${firstPathSegment === 'en' || firstPathSegment === 'ar' ? '' : ('/' + firstPathSegment)}` : locationObj.origin;

      return baseDomain;
    }else{
      return !environment.production ? 'http://localhost:4200' : this.serverData.domainURL;
    }
  }

  async printHTML(htmlString: string, fileName: string = 'file.pdf'): Promise<boolean> {
    try {
      // Create a hidden container dynamically
      const container = document.getElementById('pdf-container')!;

      // Insert the HTML
      container.innerHTML = htmlString;

      // Wait for render
      await new Promise(resolve => setTimeout(resolve, 150));

      // Capture with html2canvas
      const canvas = await html2canvas(container, {
        scale: 2,
        useCORS: true
      });

      const img = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

      pdf.addImage(img, 'PNG', 0, 0, pdfWidth, pdfHeight);
      pdf.save(fileName);

      return true;

    } catch (error) {
      console.error('PDF Error:', error);
      return false;
    }
  }

async printImage(htmlString: string, fileName: string = 'file.png'): Promise<boolean> {
  try {
    // Create a hidden container dynamically
    const container = document.getElementById('pdf-container')!;

    // Insert the HTML
    container.innerHTML = htmlString;

    // Set specific dimensions matching the Android XML layout
    // The XML has width="match_parent" and height="600dp"
    // Assuming typical Android density, 600dp ≈ 900-1200px depending on device
    // For consistency, we'll use a standard width
    const targetWidth = 613; // Standard mobile width in pixels
    const targetHeight = 1024; // 600dp converted to pixels (using 1.5 density factor)
    
    container.style.width = `${targetWidth}px`;
    container.style.height = `${targetHeight}px`;

    // Wait for render
    await new Promise(resolve => setTimeout(resolve, 150));

    // Capture with html2canvas at exact dimensions
    const canvas = await html2canvas(container, {
      scale: 1, // Use scale 1 since we're setting exact dimensions
      useCORS: true,
      backgroundColor: '#ffffff',
      width: targetWidth,
      height: targetHeight
    });

    // Convert canvas to blob
    canvas.toBlob((blob) => {
      if (blob) {
        // Create download link
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName.endsWith('.png') ? fileName : `${fileName}.png`;
        
        // Trigger download
        document.body.appendChild(link);
        link.click();
        
        // Cleanup
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
    }, 'image/png', 1.0);

    return true;

  } catch (error) {
    console.error('Image Export Error:', error);
    return false;
  }
}


}
